import React, { Component } from 'react';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

import StyleHelper from '../../shared/helpers/StyleHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight';

import '../../shared/components/DayList.css';

class DayList extends Component {
  constructor(props) {
    super(props);
    this.state = { startOffset: 0, animation: '' };
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.numberOfDaysLookahead !== this.props.numberOfDaysLookahead ||
      prevProps.visibleWindowSize !== this.props.visibleWindowSize
    ) {
      const windowSize = this.getWindowSize();
      const pages = Math.ceil(this.props.numberOfDaysLookahead / windowSize);
      const maxOffset = Math.max(0, (pages - 1) * windowSize);
      if (this.state.startOffset > maxOffset) {
        this.setState({ startOffset: maxOffset });
      }
    }
  }

  getWindowSize() {
    /* We have no concept of "visible window" when the Timetable brand only supports
    seeing up to 8 days in the future. In that case, we just show all days and have no
    notion of scrolling right/left to see more days */
    const days = this.props.numberOfDaysLookahead;
    return days > 8 ? this.props.visibleWindowSize : days;
  }

  onDateClick(date) {
    if (this.props.date !== date && typeof this.props.onChange === 'function') {
      this.props.onChange(date);
    }
  }

  adjustOffset(amount) {
    const windowSize = this.getWindowSize();
    const pages = Math.ceil(this.props.numberOfDaysLookahead / windowSize);
    const maxOffset = Math.max(0, (pages - 1) * windowSize);
    let nextOffset = this.state.startOffset + amount;
    if (nextOffset < 0) {
      nextOffset = 0;
    }
    if (nextOffset > maxOffset) {
      nextOffset = maxOffset;
    }
    this.setState({
      startOffset: nextOffset,
      animation: amount > 0 ? 'slide-left' : 'slide-right',
    });
  }

  renderArrow(direction, maxOffset) {
    const arrowClass = StyleHelper.applyCustomStyle(this.props.arrowStyle, '.DayListArrow')
      ? 'custom'
      : '';

    const arrowVariables = {};
    if (this.props.arrowColor) {
      arrowVariables['--DayListArrowColor'] = this.props.arrowColor;
    }
    if (this.props.arrowHoverColor) {
      arrowVariables['--DayListArrowHoverColor'] = this.props.arrowHoverColor;
    }

    const isLeft = direction === 'left';
    const disabled = isLeft ? this.state.startOffset === 0 : this.state.startOffset >= maxOffset;
    const tooltip = isLeft
      ? disabled
        ? undefined
        : 'Show previous week'
      : disabled
      ? undefined
      : 'Show following week';

    return (
      <button
        className={`DayListArrow ${direction} ${arrowClass}`}
        onClick={() => this.adjustOffset(isLeft ? -this.getWindowSize() : this.getWindowSize())}
        disabled={disabled}
        style={arrowVariables}
        data-tooltip={tooltip}
      >
        <FontAwesomeIcon
          icon={isLeft ? faChevronLeft : faChevronRight}
          style={{
            height: 20,
            width: 20,
            transition: 'all 0.25s',
          }}
        />
      </button>
    );
  }

  render() {
    const days = this.props.numberOfDaysLookahead;
    const windowSize = this.getWindowSize();
    const showArrows = days !== windowSize;
    const pages = Math.ceil(days / windowSize);
    const maxOffset = Math.max(0, (pages - 1) * windowSize);
    const start = this.state.startOffset;
    const selectedDate = this.props.date;
    const dayList = [];
    const isBranded = StyleHelper.applyCustomStyle(
      this.props.brandStyle,
      '.DayList li.selected.branded'
    )
      ? 'branded'
      : '';

    let dt = DateTime.local().plus({ days: start });
    for (let i = start; i < start + windowSize && i < days; i++) {
      const dateString = dt.toISODate();
      dayList.push(
        <li
          key={i}
          className={`${dateString === selectedDate ? 'selected' : ''} ${isBranded}`}
          onClick={() => this.onDateClick(dateString)}
        >
          <span className="dayName">{dt.toLocaleString({ weekday: 'short' })}</span>
          <span className="dayAndMonth">
            {dt.toLocaleString({ day: '2-digit', month: 'short' })}
          </span>
        </li>
      );
      dt = dt.plus({ days: 1 });
    }

    const listClasses = `DayList ${this.state.animation} ${showArrows ? 'with-arrows' : ''}`;
    const containerClasses = `DayListContainer ${showArrows ? 'with-arrows' : ''}`;

    return (
      <div className={containerClasses}>
        {showArrows ? this.renderArrow('left', maxOffset) : null}
        <ul className={listClasses} onAnimationEnd={() => this.setState({ animation: '' })}>
          {dayList}
        </ul>
        {showArrows ? this.renderArrow('right', maxOffset) : null}
      </div>
    );
  }
}

DayList.propTypes = {
  date: PropTypes.string,
  onChange: PropTypes.func,
  numberOfDaysLookahead: PropTypes.number,
  brandStyle: PropTypes.object,
  visibleWindowSize: PropTypes.number,
  arrowStyle: PropTypes.object,
  arrowColor: PropTypes.string,
  arrowHoverColor: PropTypes.string,
};

DayList.defaultProps = {
  visibleWindowSize: 7,
};

export default DayList;
